/** Month arithmetic and the two month reads.
 *
 *  The Monthly Log has two faces and they come from different places, which is
 *  the whole reason it is two faces:
 *
 *  - The **calendar** face is dates, so it reads a date range.
 *  - The **inbox** face is month-scoped tasks, which live on `active_month` and
 *    are *deliberately excluded* from date-range queries. They only come back
 *    from `?active_month=YYYY-MM`.
 *
 *  A task therefore appears in exactly one of the two faces, never both.
 */

import { request, type Session } from './client';
import type { WireEntry, WireEventRange } from './wire';
import { calendarWindowEnd } from './adapter';

export interface MonthRange {
  /** `YYYY-MM`. */
  month: string;
  from: string;
  to: string;
  /** Every date in the month, in order. */
  days: string[];
}

export function monthOf(journalDate: string): MonthRange {
  const month = journalDate.slice(0, 7);
  return monthFrom(month);
}

export function monthFrom(month: string): MonthRange {
  const [y, m] = month.split('-').map(Number);
  const year = y ?? 1970;
  const index = (m ?? 1) - 1;
  const count = new Date(Date.UTC(year, index + 1, 0)).getUTCDate();
  const days = Array.from({ length: count }, (_, i) => {
    const day = String(i + 1).padStart(2, '0');
    return `${month}-${day}`;
  });
  return { month, from: days[0] ?? '', to: days[count - 1] ?? '', days };
}

export function shiftMonth(month: string, direction: -1 | 1): string {
  const [y, m] = month.split('-').map(Number);
  const at = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + direction, 1));
  return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
}

const MONTHS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

export function monthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
}

/** The month's task inbox. Never a date range — these are `active_month` tasks
 *  and a date-range query will not return them. */
export async function monthTasks(session: Session, month: string): Promise<WireEntry[]> {
  return (
    await request<{ items: WireEntry[] }>(
      session,
      `/v1/entries?kind=task&active_month=${month}&limit=100`,
    )
  ).body.items;
}

export async function monthEvents(
  session: Session,
  range: MonthRange,
): Promise<WireEventRange | null> {
  try {
    return (
      await request<WireEventRange>(
        session,
        `/v1/calendar/events?from=${range.from}&to=${calendarWindowEnd(range.to)}`,
      )
    ).body;
  } catch {
    // No provider configured is a state, not a failure.
    return null;
  }
}

/** Dated tasks across the month, for the calendar face's day rows. */
export async function monthDatedTasks(
  session: Session,
  range: MonthRange,
): Promise<WireEntry[]> {
  return (
    await request<{ items: WireEntry[] }>(
      session,
      `/v1/entries?kind=task&from=${range.from}&to=${range.to}&limit=100`,
    )
  ).body.items;
}
